type RecaptchaEnterprise = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: { enterprise?: RecaptchaEnterprise };
  }
}

// Same site key, action and placeholder-token retry as the Login Server's <login-page> web
// component: the Login Server assesses the token against the tenant's key, so the two must match.
const siteKey = import.meta.env['VITE_LOGIN_SERVER_CAPTCHA_KEY'];
const MIN_TOKEN_LENGTH = 200;
const MAX_TOKEN_ATTEMPTS = 3;
const TOKEN_TIMEOUT_MS = 5_000;
let recaptchaPromise: Promise<RecaptchaEnterprise> | undefined;

const loadRecaptcha = (key: string) =>
  new Promise<RecaptchaEnterprise>((resolve, reject) => {
    let script: HTMLScriptElement | undefined;
    // A script that loads but never becomes ready must not stay cached as a promise that never settles.
    const timer = setTimeout(() => {
      script?.remove();
      reject(new Error('reCAPTCHA Enterprise did not become ready in time.'));
    }, TOKEN_TIMEOUT_MS);
    const whenReady = () => {
      const enterprise = window.grecaptcha?.enterprise;
      if (!enterprise) {
        clearTimeout(timer);
        return reject(new Error('reCAPTCHA Enterprise did not initialize.'));
      }
      enterprise.ready(() => {
        clearTimeout(timer);
        resolve(enterprise);
      });
    };

    // The <login-page> web component may already have loaded the script with the same key.
    if (window.grecaptcha?.enterprise?.execute) return whenReady();

    script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${key}`;
    script.async = true;
    script.onload = whenReady;
    script.onerror = () => {
      clearTimeout(timer);
      script?.remove();
      reject(new Error('Failed to load reCAPTCHA Enterprise.'));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    // Retry the script load on the next call instead of caching the failure.
    recaptchaPromise = undefined;
    throw error;
  });

/**
 * Returns a fresh invisible reCAPTCHA Enterprise token (single use, valid ~2 minutes), or undefined
 * when no site key is configured or reCAPTCHA cannot produce one in time. The request then goes out
 * without one, so only a login that actually needs the captcha fails.
 */
export const getCaptchaToken = async (): Promise<string | undefined> => {
  if (!siteKey) return undefined;

  const getToken = async () => {
    const enterprise = await (recaptchaPromise ??= loadRecaptcha(siteKey));
    let token = '';
    for (let attempt = 0; attempt < MAX_TOKEN_ATTEMPTS && token.length < MIN_TOKEN_LENGTH; attempt += 1) {
      token = await enterprise.execute(siteKey, { action: 'login' });
    }
    return token.length >= MIN_TOKEN_LENGTH ? token : undefined;
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      getToken(),
      new Promise<undefined>((resolve) => { timer = setTimeout(resolve, TOKEN_TIMEOUT_MS); }),
    ]);
  } catch (error) {
    console.warn('Unable to get a reCAPTCHA token', error);
    return undefined;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Headers for a route that can mint Login Server tokens through its captcha-protected /login.
 * Call it inside the request function so every attempt (including a retry) sends a fresh token.
 */
export const getCaptchaHeaders = async (): Promise<Record<string, string>> => {
  const token = await getCaptchaToken();
  return token ? { 'X-Captcha-Token': token } : {};
};

// unified-api's code for a Login Server captcha rejection; worth one retry, which re-runs the
// mutation function and so sends a fresh token.
export const isCaptchaFailure = (error: unknown) =>
  (error as { data?: { data?: { code?: unknown } } } | undefined)?.data?.data?.code === 'LOGIN_SERVER_CAPTCHA_FAILED';
